/**
 * Encryption context provider - React state management
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/contexts/auth';
import type { EncryptionContextType } from './types';
import {
  getUnauthenticatedState,
  getReadyState,
  getFailedState,
  performEncryptionHealthCheck
} from './operations';

const EncryptionContext = createContext<EncryptionContextType | undefined>(undefined);

interface EncryptionProviderProps {
  children: ReactNode;
}

export const EncryptionProvider = ({ children }: EncryptionProviderProps) => {
  const [isEncryptionReady, setIsEncryptionReady] = useState(false);
  const [isCheckingEncryption, setIsCheckingEncryption] = useState(false);
  const [encryptionError, setEncryptionError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    // Only run health check if user is authenticated
    if (!user) {
      console.log('[EncryptionContext] No user - skipping encryption check');
      const state = getUnauthenticatedState();
      setIsCheckingEncryption(state.isCheckingEncryption);
      setIsEncryptionReady(state.isEncryptionReady);
      setEncryptionError(state.encryptionError);
      return;
    }

    const initializeEncryption = async () => {
      setIsCheckingEncryption(true);
      
      const result = await performEncryptionHealthCheck(3);
      
      if (result.success) {
        const state = getReadyState();
        setIsEncryptionReady(state.isEncryptionReady);
        setEncryptionError(state.encryptionError);
      } else {
        const state = getFailedState(result.errorMessage || 'Erro desconhecido');
        setIsEncryptionReady(state.isEncryptionReady);
        setEncryptionError(state.encryptionError);
      }
      
      setIsCheckingEncryption(false);
    };

    initializeEncryption();
  }, [user]);

  return (
    <EncryptionContext.Provider value={{ isEncryptionReady, isCheckingEncryption, encryptionError }}>
      {children}
    </EncryptionContext.Provider>
  );
};

export const useEncryption = () => {
  const context = useContext(EncryptionContext);
  if (!context) {
    throw new Error('useEncryption must be used within EncryptionProvider');
  }
  return context;
};

export { EncryptionContext };
